/* eslint-disable import/no-extraneous-dependencies */
import { encodeDelimitedArray, decodeDelimitedArray, useQueryParams as tempUseQueryParams } from "use-query-params";

/**
 * getTermSubTerms returns a function that filters a given @filterTaxonomyTree to find a term that matches the provided @termName
 * If the term is found, it returns an array of its subTerms. If the @nameOnly parameter is set to true (default),
 * it returns an array of the titles of the subTerms. If nameOnly is set to false, it returns the subTerm objects
 */
export const getTermSubTerms = (termName, filterTaxonomyTree, nameOnly = true) => {
    const term = filterTaxonomyTree?.filter((item) => item.title === termName)?.[0];
    return term?.subTerms.map((subTerm) => (nameOnly ? subTerm.title : subTerm));
};

/**
 * getFilterTaxonomyTree returns a function that filters the provided @data to find a taxonomy that matches the provided @taxonomyName
 * If the taxonomy is found, it returns an array of its terms
 */
export const getFilterTaxonomyTree = (data, locale, taxonomyName) =>
    // Get relevant taxonomy terms.
    data?.nodes?.filter((taxonomy) => taxonomy.title === taxonomyName)[0]?.terms;

/**
 * PipeArrayDelimiter is an object that contains two methods: encode and decode
 * The encode method takes an array and returns a string where the array elements are delimited by a pipe ("|")
 * If the array is empty, it returns undefined
 * The decode method takes a string where elements are delimited by a pipe ("|") and returns an array of these elements
 */
export const PipeArrayDelimiter = {
    encode: (array) => (array.length > 0 ? encodeDelimitedArray(array, "|") : undefined),
    decode: (arrayStr) => decodeDelimitedArray(arrayStr, "|"),
};

/**
 * useQueryParams is a function that takes a @name a @paramConfig and an optional @updateType (default is "replaceIn")
 * It uses the tempUseQueryParams function to get a value and a setter for the given name and paramConfig
 * It returns an array containing the value and a new setter function that sets the state with the given updateType
 */
export const useQueryParams = (name, paramConfig, updateType = "replaceIn") => {
    const [value, setter] = tempUseQueryParams(name, paramConfig);
    return [value, (state) => setter(state, updateType)];
};

/**
 * filterAndFlattenNonEmptyValues is a function that takes an @obj and an array of keys to ignore
 * It filters the object's keys that are not included in the @keysToIgnore and have non-empty values
 * It then maps the filtered keys to their corresponding values in the object and flattens the result into a single array
 */
export const filterAndFlattenNonEmptyValues = (obj, keysToIgnore) => {
    return Object.keys(obj)
        .filter(
            (key) =>
                !keysToIgnore.includes(key) &&
                ((typeof obj[key] === "string" && obj[key] !== "") || (Array.isArray(obj[key]) && obj[key].length > 0))
        )
        .map((key) => obj[key])
        .flat();
};
